// =============================================================================
// ui/tabs/CoachDimensions.tsx — four reads that are not fixable leaks.
// -----------------------------------------------------------------------------
// ── 🚨 WHY THESE ARE NOT IN SPOT IT ──
//
// A leak has a dollar cost and a rule that closes it. These four describe the
// EDGE — whether one exists, how much of each move it keeps, which reads carry
// it, and which pattern-and-exit combination is the engine. None of them is a
// habit, so none of them ever gets a Fix-it button, and putting them in the
// ledgers would have invited a rule against a number no rule can move.
//
// ── 🚨 EVERY PANEL SAYS WHY IT IS EMPTY, IN ITS OWN WORDS ──
//
// The same doctrine the leak rows follow. "Needs 5 trades carrying both MAE and
// MFE — you have 2" and "this client cannot measure that" are different facts,
// and a shared blank is how a trader concludes the feature is broken.
//
// ⚠ RISK OF RUIN IS SCOPED TO THE SELECTED ACCOUNT AND WINDOW, and the old
// client falls back to the account's WHOLE history when the period is thin.
// That fallback is not ported — it needs a fifth trades read — so a thin window
// says so instead of quietly answering a different question. Stated on the card.
// =============================================================================

import { num, pct } from '../format';
import { EduMark } from '../components/EduMark';
import type { ReactNode } from 'react';
import {
  CONFLUENCE_MIN_SAMPLE, ERATIO_MIN_SAMPLE, PAIRING_MIN_SAMPLE,
  ROR_MIN_DAYS, ROR_STABLE_DAYS, ruinBand,
  type ConfluenceRank, type EdgeQuality, type Pairings, type RuinResult,
} from '../../selectors/coachDimensions';
import { coachWindowPhrase, type CoachWindowId } from '../../selectors/leaks';

export interface DimensionData {
  ruin: RuinResult | null;
  ruinDays: number;
  /** Null when no single account is selected — ruin is an account's number. */
  ruinAccount: string | null;
  ruinBlocked: string | null;
  edge: EdgeQuality;
  capturePct: number | null;
  captureN: number;
  confluences: ConfluenceRank[];
  pairs: Pairings;
}

export function CoachDimensions(
  { d, window }: { d: DimensionData; window: CoachWindowId },
) {
  return (
    <section className="card" data-testid="coach-dimensions">
      <div className="coach-step-head">
        <div>
          <div className="coach-step-t">Four more lenses</div>
          <p className="muted">
            Reads that aren’t single fixable leaks — whether the edge is there at
            all, and where it lives. Over {coachWindowPhrase(window)}.
          </p>
        </div>
      </div>

      <div className="coach-dims">
        <Ruin d={d} />
        <Capture d={d} />
        <Confluences rows={d.confluences} />
        <Pairs pairs={d.pairs} />
      </div>
    </section>
  );
}

function Panel(
  { title, eduKey, children }: { title: string; eduKey: string; children: ReactNode },
) {
  return (
    <div className="coach-dim" data-dim={title}>
      {/* ⚠ EVERY PANEL CARRIES ITS OWN `?`. These four are the least
          self-explanatory numbers on the tab — a resampled ruin probability and
          an E-ratio mean nothing without their definitions — and beta marks all
          four for exactly that reason. */}
      <div className="muted coach-k">{title}<EduMark eduKey={eduKey} /></div>
      {children}
    </div>
  );
}

const Empty = ({ children }: { children: ReactNode }) => (
  <p className="muted coach-dim-empty">{children}</p>
);

/**
 * A signed R, formatted from the ROUNDED value.
 *
 * ⚠ `-0.00R` IS A LIE, and it shipped for one build. An average of −0.0004
 * rounds to zero but keeps its sign, so the worst pairing rendered as a loss
 * when it was flat — on Josh's real book, `VWAP PDC × Discretionary` showed
 * "−0.00R". Deciding the sign from the rounded number is the fix; it is the same
 * family as the `−+$310` double sign already recorded against the old client.
 */
function signedR(v: number): string {
  const r = Math.round(v * 100) / 100;
  const sign = r > 0 ? '+' : r < 0 ? '−' : '';
  return `${sign}${num(Math.abs(r), 2)}R`;
}

// ---------------------------------------------------------------------------

function Ruin({ d }: { d: DimensionData }) {
  return (
    <Panel title="Risk of ruin" eduKey="coach.dimension.risk-of-ruin">
      {d.ruinBlocked ? (
        <Empty>{d.ruinBlocked}</Empty>
      ) : d.ruin === null ? (
        // ⚠ NAMES THE ACTUAL BLOCKER. Listing both reasons every time sends the
        // trader to check a drawdown setting that is already there, or to trade
        // more days when the real gap is a rule the firm set and they never
        // entered.
        <Empty>
          {d.ruinDays < ROR_MIN_DAYS ? (
            <>Needs at least {num(ROR_MIN_DAYS, 0)} trading days on{' '}
              {d.ruinAccount ?? 'this account'} in the window — there{' '}
              {d.ruinDays === 1 ? 'is' : 'are'} {num(d.ruinDays, 0)}.</>
          ) : (
            <>{d.ruinAccount ?? 'This account'} has no max drawdown set, so there
              is no floor to model. Add it on the Accounts tab and this fills in.</>
          )}
        </Empty>
      ) : (
        <>
          <div className={`coach-dim-v ${ruinBand(d.ruin.blowPct)}`}>
            {pct(d.ruin.blowPct, 0)}
          </div>
          <p className="muted coach-dim-note">
            {/* ⚠ THE METHOD IS NAMED. A percentage with no method behind it
                invites the trader to read it as a fact about the future rather
                than a resample of their own past. */}
            Resampling {num(d.ruin.days, 0)} of your own trading days over the
            next {num(30, 0)}, {num(d.ruin.sims, 0)} times. That share of runs hit
            the account’s trailing drawdown.
            {d.ruin.preliminary ? (
              <> <b>Preliminary</b> — under {num(ROR_STABLE_DAYS, 0)} days in the
                pool, so treat it as a direction, not a forecast.</>
            ) : null}
          </p>
        </>
      )}
    </Panel>
  );
}

function Capture({ d }: { d: DimensionData }) {
  const e = d.edge;
  return (
    <Panel title="Capture & edge" eduKey="coach.dimension.capture">
      {d.capturePct === null && e.eRatio === null ? (
        <Empty>
          Needs chart or excursion data. {e.n === 0
            ? 'No trade in this window carries both an MAE and an MFE.'
            : `${num(e.n, 0)} trade${e.n === 1 ? '' : 's'} carr${e.n === 1 ? 'ies' : 'y'} both — the E-ratio needs ${num(ERATIO_MIN_SAMPLE, 0)}.`}
        </Empty>
      ) : (
        <>
          <div className="coach-dim-v">
            {d.capturePct === null ? '—' : pct(d.capturePct, 0)}
          </div>
          <p className="muted coach-dim-note">
            The median share of the favourable move you kept, on{' '}
            {num(d.captureN, 0)} trade{d.captureN === 1 ? '' : 's'} that reached 1R.
          </p>
          <div className="coach-dim-sub">
            <span className="muted">E-ratio</span>
            <b className="mono">{e.eRatio === null ? '—' : num(e.eRatio, 2)}</b>
            <span className="muted">
              {e.eRatio === null
                ? `needs ${num(ERATIO_MIN_SAMPLE, 0)} trades with both excursions`
                : e.eRatio >= 1.2
                  ? 'above 1.2 — the move goes your way more than against you'
                  : 'under 1.2 — the move goes against you about as often'}
            </span>
          </div>
        </>
      )}
    </Panel>
  );
}

function Confluences({ rows }: { rows: ConfluenceRank[] }) {
  return (
    <Panel title="Confluences carrying the edge" eduKey="coach.dimension.confluence">
      {!rows.length ? (
        <Empty>
          No confluence has {num(CONFLUENCE_MIN_SAMPLE, 0)} tagged trades with a
          logged R in this window. Tag the reads you took and this ranks them.
        </Empty>
      ) : (
        <div className="coach-dim-rows">
          {rows.map((r) => (
            <div className="coach-dim-row" key={r.id}>
              <span className="grow">{r.name}</span>
              {/* ⚠ THE MEDIAN, and the label says so. A trader comparing it
                  against an average elsewhere in the app would otherwise read
                  two different statistics as one. */}
              <b className="mono">{signedR(r.medianR)}</b>
              <span className="muted coach-dim-n">median · {num(r.n, 0)}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function Pairs({ pairs }: { pairs: Pairings }) {
  return (
    <Panel title="Pattern × exit" eduKey="coach.dimension.pattern-exit">
      {!pairs.best ? (
        <Empty>
          No pattern-and-exit combination has {num(PAIRING_MIN_SAMPLE, 0)} trades
          with a logged R yet. A pairing needs BOTH tagged — the same setup taken
          with a different exit is a different trade.
        </Empty>
      ) : (
        <div className="coach-dim-rows">
          <div className="coach-dim-row">
            <span className="grow">
              {pairs.best.pattern} × {pairs.best.exit}
            </span>
            <b className={`mono ${pairs.best.avgR > 0 ? 'good' : ''}`}>
              {signedR(pairs.best.avgR)}
            </b>
            <span className="muted coach-dim-n">best · {num(pairs.best.n, 0)}</span>
          </div>
          {pairs.worst ? (
            <div className="coach-dim-row">
              <span className="grow">
                {pairs.worst.pattern} × {pairs.worst.exit}
              </span>
              <b className={`mono ${Math.round(pairs.worst.avgR * 100) < 0 ? 'bad' : ''}`}>
                {signedR(pairs.worst.avgR)}
              </b>
              <span className="muted coach-dim-n">worst · {num(pairs.worst.n, 0)}</span>
            </div>
          ) : (
            // ⚠ ONE CELL IS THE BEST AND NOTHING IS THE WORST. Naming the same
            // pairing twice would read as a contradiction.
            <p className="muted coach-dim-note">
              Only one combination has enough trades to judge, so there is no
              worst to name yet.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}
